import { effectivePrice } from '@hunk/shared/api/shop';
import { Loader2, SearchX } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';

import { ProductGrid } from '@/components/ProductCard';
import { useShop } from '@/hooks/useShop';
import { cn } from '@/lib/utils';

type Sort = 'default' | 'new' | 'price-asc' | 'price-desc' | 'sale';
const SORTS: { key: Sort; label: string }[] = [
  { key: 'default', label: 'מומלצים' },
  { key: 'new', label: 'הכי חדשים' },
  { key: 'sale', label: 'במבצע' },
  { key: 'price-asc', label: 'מחיר: מהזול' },
  { key: 'price-desc', label: 'מחיר: מהיקר' }
];

/** Hebrew-friendly search: every word must appear in the name, SKU or description */
const matches = (text: string, words: string[]) => {
  const t = text.toLowerCase();
  return words.every((w) => t.includes(w));
};

/** home (/), a category (/category/:slug) and search (?q=) */
export function HomePage() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const q = (params.get('q') ?? '').trim();
  const { products, categories, loading, error } = useShop();
  const [sort, setSort] = useState<Sort>('default');
  const category = slug ? categories.find((c) => c.slug === decodeURIComponent(slug)) : undefined;

  const shown = useMemo(() => {
    const words = q.toLowerCase().split(/\s+/).filter(Boolean);
    let list = products.filter((p) => (!category || p.categoryIds.includes(category.id)) && (!words.length || matches(`${p.name} ${p.sku ?? ''} ${p.description}`, words)));
    if (sort === 'sale') list = list.filter((p) => p.salePrice != null);
    const by = {
      default: () => 0,
      new: (a: (typeof list)[0], b: (typeof list)[0]) => b.createdOn.localeCompare(a.createdOn),
      sale: () => 0,
      'price-asc': (a: (typeof list)[0], b: (typeof list)[0]) => effectivePrice(a) - effectivePrice(b),
      'price-desc': (a: (typeof list)[0], b: (typeof list)[0]) => effectivePrice(b) - effectivePrice(a)
    }[sort];
    // out of stock last
    return [...list].sort((a, b) => Number(b.inStock) - Number(a.inStock) || by(a, b));
  }, [products, category, q, sort]);

  if (loading)
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="text-primary size-10 animate-spin" />
      </div>
    );
  if (error) return <p className="py-24 text-center text-red-400">{error}</p>;
  if (slug && !category) return <NotFound />;

  const title = q ? `תוצאות חיפוש: "${q}"` : category ? category.name : 'המוצרים הכי חמים!!! לוהטים!!! חדשים!!!';

  return (
    <main>
      {!slug && !q && <Hero count={products.length} />}
      <section className="mx-auto max-w-[1280px] px-3 py-6 md:px-4 md:py-10">
        <h1 className="mb-4 text-center text-2xl font-extrabold md:mb-6 md:text-4xl">{title}</h1>
        <div className="scrollbar-none mb-5 flex justify-center gap-1.5 overflow-x-auto">
          {SORTS.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => setSort(s.key)}
              className={cn(
                'shrink-0 rounded-full border px-3.5 py-1.5 text-xs font-semibold md:text-sm',
                sort === s.key ? 'border-primary bg-primary text-white' : 'border-white/20 text-neutral-300 hover:text-white'
              )}
            >
              {s.label}
            </button>
          ))}
        </div>
        {shown.length ? (
          <ProductGrid products={shown} />
        ) : (
          <div className="flex flex-col items-center gap-3 py-16 text-neutral-400">
            <SearchX className="size-12" />
            <p>לא נמצאו מוצרים{q ? ` עבור "${q}"` : ''}</p>
            <Link to="/" className="text-primary font-semibold hover:underline">
              לכל המוצרים
            </Link>
          </div>
        )}
      </section>
    </main>
  );
}

function Hero({ count }: { count: number }) {
  return (
    <section className="relative overflow-hidden bg-[radial-gradient(ellipse_at_top,#4a0a0d_0%,#000_70%)]">
      <div className="mx-auto flex max-w-[1280px] flex-col items-center px-4 py-12 text-center md:py-20">
        <div className="text-6xl font-extrabold md:text-8xl">{count}</div>
        <p className="mt-2 text-2xl font-bold md:text-4xl">מוצרים ביבוא אישי</p>
        <p className="text-2xl font-bold md:text-4xl">במחירים הכי זולים בארץ</p>
        <p className="mt-5 max-w-2xl text-base text-neutral-300 md:text-xl">מתמחים בכל סוגי המוצרים וחומרי הסיכה לפיסט. משלוח דיסקרטי עד הבית או איסוף עצמי.</p>
      </div>
      <div className="bg-primary py-3 text-center text-xl font-extrabold md:text-3xl">HUNK בבעלות הבוס הגדול</div>
    </section>
  );
}

export function NotFound() {
  return (
    <div className="flex flex-col items-center gap-3 py-24 text-neutral-400">
      <SearchX className="size-12" />
      <p>הדף לא נמצא</p>
      <Link to="/" className="text-primary font-semibold hover:underline">
        חזרה לחנות
      </Link>
    </div>
  );
}
